import { useState } from "react";
import { Upload, X } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";

const tomorrow = () => new Date(Date.now() + 86400000).toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

export const HdjImportModal = ({ onClose, onDone }) => {
  const [mode, setMode] = useState("SEANCES_JOUR");
  const [date, setDate] = useState(today());
  const [rangeEnd, setRangeEnd] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const changeMode = (m) => { setMode(m); setDate(m === "SEANCES_JOUR" ? today() : tomorrow()); };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choisissez un fichier");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("mode", mode); fd.append("targetDate", date);
      if (mode === "RDV_A_VENIR" && rangeEnd) fd.append("rangeEnd", rangeEnd);
      const { data } = await api.post("/hdj/imports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import impossible");
    } finally { setBusy(false); }
  };

  const counts = result ? {
    applied: result.rows.filter((r) => r.applied).length,
    toConfirm: result.rows.filter((r) => r.matchStatus === "TO_CONFIRM" && !r.ignored).length,
    unknown: result.rows.filter((r) => r.matchStatus === "UNKNOWN" && !r.ignored).length,
    moved: result.rows.filter((r) => r.result?.action === "moved").length,
    cancelled: (result.reconciliation?.cancelled || 0) + result.rows.filter((r) => r.result?.action === "cancelled").length,
    blocked: result.reconciliation?.blocked || 0,
    declined: result.reconciliation?.patientDeclined || 0,
    ignoredPast: result.rows.filter((r) => r.ignoreReason === "date passée").length,
    days: result.coveredDates || [],
  } : null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="hdj-import-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div><h3 className="font-bold text-slate-800 text-lg">Importer le planning HDJ</h3><p className="text-xs text-slate-500 mt-1">Export de votre logiciel : Excel/CSV lu directement, PDF ou photo lu par l'IA. L'IDE vérifie ensuite dans sa relève.</p></div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="hdj-import-close"><X className="w-4 h-4" /></button>
        </div>
        {!result ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              {[["SEANCES_JOUR", "Séances du jour"], ["RDV_A_VENIR", "RDV à venir"]].map(([k, l]) => (
                <button type="button" key={k} onClick={() => changeMode(k)} data-testid={`hdj-import-mode-${k}`}
                  className={`rounded-xl border px-3 py-2 text-sm font-medium ${mode === k ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-200 text-slate-600"}`}>{l}</button>
              ))}
            </div>
            <label className="block text-xs text-slate-600">{mode === "RDV_A_VENIR" ? "Date concernée (si le fichier n'a pas de colonne date)" : "Date concernée"}
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="hdj-import-date" className="block mt-1 w-full border rounded-lg px-3 py-2 text-sm" />
            </label>
            {mode === "RDV_A_VENIR" && (
              <>
                <label className="block text-xs text-slate-600">Le fichier couvre jusqu'au (facultatif)
                  <input type="date" value={rangeEnd} min={date} onChange={(e) => setRangeEnd(e.target.value)} data-testid="hdj-import-range-end" className="block mt-1 w-full border rounded-lg px-3 py-2 text-sm" />
                  <span className="block mt-1 text-[11px] text-slate-400">À renseigner si une journée a été entièrement vidée : ses RDV Hana seront alors annulés aussi.</span>
                </label>
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="hdj-import-reconcile-hint">
                  Le dernier planning fait foi : pour les jours couverts, les RDV importés précédemment qui n'y figurent plus sont annulés automatiquement et le patient est prévenu. Les dates passées sont ignorées.
                </p>
              </>
            )}
            <label className="block text-xs text-slate-600">Fichier (CSV, XLSX, PDF, PNG, JPG)
              <input type="file" accept=".csv,.txt,.xlsx,.xlsm,.pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="hdj-import-file" className="block mt-1 w-full text-sm" />
            </label>
            <button type="submit" disabled={busy} data-testid="hdj-import-submit" className="w-full flex items-center justify-center gap-2 bg-emerald-600 text-white rounded-xl px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
              <Upload className="w-4 h-4" /> {busy ? "Lecture en cours…" : "Importer"}
            </button>
          </>
        ) : (
          <div className="space-y-3" data-testid="hdj-import-result">
            <p className="text-sm text-slate-700"><strong>{result.rows.length}</strong> ligne(s) lues ({result.extractor === "ai" ? "IA" : "fichier"}) : <strong className="text-emerald-700">{counts.applied}</strong> appliquée(s), <strong className="text-amber-700">{counts.toConfirm}</strong> à rapprocher par l'IDE, <strong className="text-slate-500">{counts.unknown}</strong> hors Hana.</p>
            {result.mode === "RDV_A_VENIR" && (
              <p className="text-xs text-slate-600" data-testid="hdj-import-reconcile-summary">
                Jours couverts : {counts.days.length > 1 ? `${counts.days[0]} → ${counts.days[counts.days.length - 1]}` : counts.days.join(", ")} — <strong>{counts.moved}</strong> RDV déplacé(s), <strong className="text-rose-700">{counts.cancelled}</strong> RDV annulé(s){counts.ignoredPast > 0 && <>, {counts.ignoredPast} ligne(s) à date passée ignorée(s)</>}.
                {counts.declined > 0 && <span className="block mt-1 text-amber-700 font-semibold" data-testid="hdj-import-declined">{counts.declined} patient(s) ont décliné leur RDV via Hana mais figurent encore au planning : à rappeler.</span>}
                {counts.blocked > 0 && <span className="block mt-1 text-rose-700 font-semibold">Annulation automatique bloquée : {counts.blocked} RDV absents du fichier (fichier incomplet ?). L'IDE est prévenue.</span>}
              </p>
            )}
            <p className="text-xs text-slate-500">Un item a été ajouté à la relève IDE pour vérification.</p>
            <button type="button" onClick={onClose} data-testid="hdj-import-done" className="w-full bg-slate-800 text-white rounded-xl px-4 py-2.5 text-sm font-semibold">Fermer</button>
          </div>
        )}
      </form>
    </div>
  );
};

export default HdjImportModal;
